import { QueryClient } from '@tanstack/react-query';

// Configuration des stale times par type de données (en millisecondes)
const STALE_TIMES = {
  // Données temps réel (IBKR)
  REALTIME: 30 * 1000,        // 30 secondes - positions, compte
  STATUS: 2 * 60 * 1000,      // 2 minutes - statut TWS (moins critique)
  
  // Données statiques
  STATIC: 5 * 60 * 1000,      // 5 minutes - catégories, organisations
  SETTINGS: 60 * 1000,         // 1 minute - paramètres utilisateur
  SESSION: 60 * 1000,          // 1 minute - session (sécurité)
  
  // Données calculées
  CALCULATED: 10 * 60 * 1000, // 10 minutes - données calculées/analyses
};

// Configuration du QueryClient avec retry logic et error handling
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time par défaut (peut être overridé par query)
      staleTime: STALE_TIMES.REALTIME,
      
      // Cache time (garde en cache même si non utilisé)
      gcTime: 5 * 60 * 1000, // 5 minutes (anciennement cacheTime)
      
      // Retry logic avec backoff exponentiel
      retry: (failureCount, error) => {
        // Ne pas retry sur erreurs 4xx (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry jusqu'à 3 fois pour erreurs serveur/réseau
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => {
        // Backoff exponentiel : 1s, 2s, 4s
        return Math.min(1000 * 2 ** attemptIndex, 4000);
      },
      
      // Refetch on window focus (désactivé pour éviter les appels inutiles)
      refetchOnWindowFocus: false,
      
      // Refetch on reconnect (activé pour récupérer après perte de connexion)
      refetchOnReconnect: true,
      
      // Refetch on mount (désactivé si données récentes)
      refetchOnMount: true,
    },
    mutations: {
      // Retry pour mutations (généralement non recommandé)
      retry: false,
    },
  },
});

// Export des stale times pour utilisation dans les hooks
export { STALE_TIMES };

// Fonction helper pour créer des query keys standardisées
export const queryKeys = {
  // IBKR / TWS
  positions: () => ['tws', 'positions'],
  account: () => ['tws', 'account'],
  accountSummary: () => ['tws', 'account-summary'],
  ibkrStatus: () => ['tws', 'status'],
  orders: () => ['tws', 'orders'],
  
  // Catégories et enrichissement
  categories: () => ['categories'],
  category: (id) => ['categories', id],
  assetEnrichment: (symbol) => ['asset-enrichment', symbol],
  assetEnrichmentBulk: (symbols) => ['asset-enrichment', 'bulk', symbols],
  
  // Organisations et utilisateurs
  organizations: () => ['organizations'],
  organization: (id) => ['organizations', id],
  organizationMembers: (orgId) => ['organizations', orgId, 'members'],
  
  // Paramètres
  settings: (orgId, userId) => ['settings', orgId, userId],
  setting: (name, orgId, userId) => ['settings', name, orgId, userId],
  
  // Session et avatar
  session: () => ['session'],
  avatar: (userId) => ['avatar', userId],
  
  // Portfolio
  valuations: (startDate, endDate) => ['portfolio', 'valuations', startDate, endDate],
  currentValuation: () => ['portfolio', 'valuations', 'current'],
  apports: (startDate, endDate, investisseur) => ['portfolio', 'apports', startDate, endDate, investisseur],
  investisseurs: (actifOnly) => ['portfolio', 'investisseurs', actifOnly],
  performance: (startDate, endDate, orgId, userId) => ['portfolio', 'performance', startDate, endDate, orgId, userId],
  
  // Analyse
  analyses: (limit) => ['analyses', limit],
  analysis: (id) => ['analyses', id],
  metaAnalysis: (forceRegenerate) => ['analyses', 'meta', forceRegenerate],
  
  // Macro
  indices: () => ['macro', 'indices'],
  commodities: () => ['macro', 'commodities'],
  
  // IB Gateway Config
  ibGatewayConfig: (orgId) => ['ib-gateway-config', orgId],
  
  // Health
  health: () => ['health'],
};

